import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ImageSkeleton from '../PlaceholderSkeleton/ImageSkeleton';
import RefreshButton from '../RefreshButton/RefreshButton';
import Title from '../Title/Title';
import {
  getRetryAfterSeconds,
  SCREENSHOT_TIMELINE_DEFAULT_LIMIT,
  SCREENSHOT_TIMELINE_MAX_LIMIT,
  useRefreshScreenshotMutation,
  useScreenshotTimelineQuery,
} from '../../queries/screenshots';
import ScreenshotStrip from './ScreenshotStrip';
import ScreenshotModal from './ScreenshotModal';
import { screenshotCaption } from './screenshotUtils';
import { ScreenshotTimelineEntry } from '../../types/ScreenshotTypes';

const syncIconStyle = {
  cursor: 'pointer',
  '&:hover': {
    color: 'info.main',
    transform: 'rotate(90deg)',
    transition: 'all 0.3s ease-in-out',
  },
};

interface ScreenshotPanelProps {
  threatId?: number;
  /** Threat name, used for the modal title and the final-URL comparison. */
  domain?: string;
  /** Skeleton height while the timeline loads. */
  skeletonHeight?: number;
  /** Header style: the cockpit card's compact subtitle, or the classic
   * page's large bar title with a divider underneath. */
  variant?: 'cockpit' | 'classic';
}

/**
 * Detail-page screenshot card body shared by the classic and cockpit views
 * (WHEEL-394): latest capture with caption, refresh / capture-now control,
 * capture history strip and the full-size modal with prev/next.
 */
const ScreenshotPanel = ({
  threatId,
  domain,
  skeletonHeight = 180,
  variant = 'cockpit',
}: ScreenshotPanelProps) => {
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const retriedRef = useRef(false);

  const limit = showAll
    ? SCREENSHOT_TIMELINE_MAX_LIMIT
    : SCREENSHOT_TIMELINE_DEFAULT_LIMIT;
  const { data, isPending, refetch } = useScreenshotTimelineQuery(threatId, {
    limit,
  });
  const refresh = useRefreshScreenshotMutation();

  const entries = useMemo<ScreenshotTimelineEntry[]>(
    () => data?.items ?? [],
    [data],
  );
  const total = data?.total ?? entries.length;

  // Selection follows the newest capture until the analyst picks another one
  // (or the picked one leaves the page after a "Show all" toggle).
  const selectedIndex = Math.max(
    0,
    entries.findIndex(entry => entry.id === selectedId),
  );
  const selected = entries[selectedIndex];

  // 429 cooldown: the button stays disabled for Retry-After seconds.
  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timer = setTimeout(() => setCooldownSeconds(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const handleRefresh = () => {
    if (typeof threatId !== 'number') return;
    refresh.mutate(threatId, {
      onSuccess: capture => setSelectedId(capture.id),
      onError: error => {
        const seconds = getRetryAfterSeconds(error);
        if ((error as any)?.response?.status === 429 && seconds) {
          setCooldownSeconds(seconds);
        }
      },
    });
  };

  const loading = typeof threatId === 'number' && isPending;
  const capturing = refresh.isPending;
  const refreshDisabled =
    typeof threatId !== 'number' || capturing || cooldownSeconds > 0;
  // Always shown when the service recorded it: the client panel shows it
  // unconditionally too, and a "differs from the domain" rule would have
  // to live in the backend to stay identical across frontends.
  const finalUrl = selected?.final_url ?? null;

  return (
    <Box data-testid="screenshot-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {variant === 'classic' ? (
          <Title size="large" title="Screenshot" />
        ) : (
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Screenshot
          </Typography>
        )}
        <Tooltip
          title={
            cooldownSeconds > 0
              ? `Refresh limit reached, try again in ${cooldownSeconds}s`
              : capturing
              ? 'Capturing…'
              : 'Take a fresh screenshot'
          }
        >
          <span data-testid="screenshot-refresh">
            <RefreshButton
              handleSyncIconClick={handleRefresh}
              isDisabled={refreshDisabled}
              SyncIconStyle={syncIconStyle}
            />
          </span>
        </Tooltip>
      </Box>
      {variant === 'classic' && <Divider sx={{ mb: 1 }} />}
      {capturing && (
        <LinearProgress
          data-testid="screenshot-capturing"
          sx={{ mb: 1, borderRadius: 1 }}
        />
      )}

      {loading ? (
        <ImageSkeleton height={skeletonHeight} />
      ) : !selected ? (
        <Box
          data-testid="screenshot-empty"
          sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: '8px',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageOutlinedIcon
              aria-label="No screenshot available"
              sx={{ fontSize: 56, color: 'text.disabled' }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              No screenshot yet
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleRefresh}
              disabled={refreshDisabled}
              data-testid="screenshot-capture-now"
              sx={{ textTransform: 'none' }}
            >
              {capturing ? 'Capturing…' : 'Capture now'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            component="img"
            src={selected.url}
            alt={`Screenshot of ${domain ?? 'threat'}`}
            onClick={() => setModalOpen(true)}
            onError={() => {
              // Expired signed URL: refetch once to get fresh ones.
              if (retriedRef.current) return;
              retriedRef.current = true;
              refetch();
            }}
            data-testid="screenshot-image"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              border: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              display: 'block',
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="screenshot-caption"
          >
            {screenshotCaption(selected)}
          </Typography>
          {finalUrl && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="screenshot-final-url"
              sx={{ wordBreak: 'break-all' }}
            >
              {finalUrl}
            </Typography>
          )}
          {entries.length > 1 && (
            <ScreenshotStrip
              entries={entries}
              selectedId={selected.id}
              onSelect={setSelectedId}
              total={total}
              showingAll={showAll}
              onShowAll={() => setShowAll(true)}
            />
          )}
        </Box>
      )}

      <ScreenshotModal
        open={modalOpen && !!selected}
        onClose={() => setModalOpen(false)}
        entries={entries}
        index={selectedIndex}
        onIndexChange={index => setSelectedId(entries[index]?.id ?? null)}
        domain={domain}
      />
    </Box>
  );
};

export default ScreenshotPanel;
